import { useI18n } from '@runtime/core/i18n/i18n'
import { useLocation } from 'react-router-dom'
import { homeDestinations } from '@/routes/home/homeDestinations'
import {
  isPlayNavigationActive,
  type PlayNavigationDestination,
} from './playNavigationDestinations'
import PlayNavigationIcon from './PlayNavigationIcon'
import styles from './PlayNavigationItem.module.scss'
import PlayNavigationLink from './PlayNavigationLink'

export default function PlayNavigationItem({
  item,
  mobile,
  onNavigate,
}: {
  item: PlayNavigationDestination
  mobile: boolean
  onNavigate?: () => void
}) {
  const { t, locale } = useI18n()
  const { pathname } = useLocation()
  const { id, path } = item
  const label = t(`common.playNavigation.${id}`)
  const visibleLabel = mobile ? t(`common.playNavigation.mobile.${id}`) : label
  const accent = 'accent' in item ? item.accent : null
  const icon = <PlayNavigationIcon id={id} />

  if (id === 'gateway' || id === 'workshops') {
    const destination = homeDestinations(locale)
    return (
      <li>
        <a
          className={`${styles.link} ${mobile ? styles.mobile : styles.desktop}`}
          href={id === 'workshops' ? destination.workshops : destination.universe}
          aria-label={`${t(id === 'workshops' ? 'home.welcome.workshops' : 'home.welcome.discover')}${destination.fallback ? ` (${t('home.welcome.frenchSite')})` : ''}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onNavigate}
          data-testid={`play-nav-${id}`}
        >
          {icon}
          <span className={styles.label}>
            {mobile
              ? visibleLabel
              : t(id === 'workshops' ? 'home.welcome.workshops' : 'home.welcome.discover')}
            {mobile && destination.fallback && <small> FR</small>}
            {!mobile && destination.fallback && <small> — {t('home.welcome.frenchSite')}</small>} ↗
          </span>
        </a>
      </li>
    )
  }

  return (
    <li>
      <PlayNavigationLink
        to={path}
        onNavigate={onNavigate}
        className={[
          styles.link,
          accent && styles.tile,
          accent && styles[accent],
          mobile ? styles.mobile : styles.desktop,
        ]
          .filter(Boolean)
          .join(' ')}
        aria-label={
          mobile && id === 'tools' && visibleLabel !== label ? `${visibleLabel} — ${label}` : label
        }
        aria-current={isPlayNavigationActive(pathname, path) ? 'page' : undefined}
        data-testid={`play-nav-${id}`}
      >
        {accent ? (
          <span className={styles.piece} data-testid="play-nav-piece">
            {icon}
          </span>
        ) : (
          icon
        )}
        <span className={styles.label}>{visibleLabel}</span>
      </PlayNavigationLink>
    </li>
  )
}
